import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, useTheme } from '@mui/material';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import UserService from '../services/UserService';

interface EditUserDetailsDialogProps {
  open: boolean;
  onClose: () => void;
}

const EditUserDetailsDialog: React.FC<EditUserDetailsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const userCtx = useContext(UserContext);
  const [cookies] = useCookies(['access_token']);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFirstName(userCtx?.user?.first_name || '');
      setLastName(userCtx?.user?.last_name || '');
      setEmail(userCtx?.user?.email || '');
      setPhone(userCtx?.user?.phone || '');
    }
  }, [open, userCtx?.user]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMsg(null);
      // Self-service endpoint accepts subset of fields
      const payload = {
        ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
        ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      } as any;

      if (Object.keys(payload).length === 0) {
        setErrorMsg('No editable fields supplied');
        return;
      }

      await UserService.updateMyProfile(cookies.access_token, payload);
      // Refresh from backend to ensure consistency
      const me = await UserService.getMe(cookies.access_token);
      const updatedUser = { ...userCtx?.user, ...me };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      userCtx?.setUser(updatedUser as any);
      onClose();
    } catch (e) {
      // For now, just console error; could add snackbar/toast
      console.error('Failed to update profile', e);
      const msg = (e as any)?.response?.data || (e as any)?.message || 'Failed to update user details';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="edit-user-details-title">
      <DialogTitle id="edit-user-details-title">{t('edit_user_details') || 'Edit user details'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('firstname') || 'First name'}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
          />
          <TextField
            label={t('lastname') || 'Last name'}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
          />
          <TextField
            label={t('email_address') || 'Email'}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            type="email"
            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
          />
          <TextField
            label={t('phone') || 'Phone'}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3 }}>
        <Button onClick={onClose}>{t('cancel') || 'Cancel'}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{t('save') || 'Save'}</Button>
      </DialogActions>
      {errorMsg && (
        <div style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16, color: theme.palette.error.main }}>
          {String(errorMsg)}
        </div>
      )}
    </Dialog>
  );
};

export default EditUserDetailsDialog;
